import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend,
} from 'recharts'
import {
  Mail, FolderOpen, Bell, Clock, AlertTriangle, Inbox,
  Loader2, ArrowRight, ChevronRight, BarChart3, AreaChart as AreaIcon,
  Table2, Calendar, ListChecks, Tag, TrendingUp, CheckCircle2,
  Layers, ShieldAlert, Send,
} from 'lucide-react'
import { Card, Badge, Button } from '../atoms'
import { cn } from '../atoms'
import { LiveIndicator } from '../molecules/LiveIndicator'
import { IndicatorCard } from '../molecules/IndicatorCard'
import { useDashboardOverview } from '../../hooks/useDashboard'
import { useAuth } from '../../store/auth.store'
import { useCompany } from '../../hooks/useCompany'
import { useImpersonation } from '../../store/impersonation.store'

type BarAreaTable = 'bar' | 'area' | 'table'
type DonutBarTable = 'donut' | 'bar' | 'table'
type DateRange = '7d' | '30d' | '90d' | 'all'

const RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: '7d', label: '7 días' },
  { value: '30d', label: '30 días' },
  { value: '90d', label: '90 días' },
  { value: 'all', label: 'Todo' },
]

const CRITICIDAD_COLORS: Record<string, string> = {
  CRITICO_MAXIMO: '#dc2626',
  CRITICO: '#ea580c',
  ALTO: '#d97706',
  MEDIO: '#2563eb',
  BAJO: '#059669',
  INFORMATIVO: '#6b7280',
}

const CRITICIDAD_ORDER = ['CRITICO_MAXIMO', 'CRITICO', 'ALTO', 'MEDIO', 'BAJO', 'INFORMATIVO']

export function DashboardPage() {
  return <ExecutiveDashboard />
}

function ExecutiveDashboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: company } = useCompany()
  const { impersonateMailboxId } = useImpersonation()
  const [range, setRange] = useState<DateRange>('30d')
  const { data: overview, isLoading, isError, refetch, dataUpdatedAt, isFetching } = useDashboardOverview(range, impersonateMailboxId || undefined)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="sr-only">Cargando dashboard...</span>
      </div>
    )
  }

  if (isError || !overview) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <p className="text-sm font-medium text-slate-700 dark:text-gray-300">
          No se pudo cargar la información del dashboard
        </p>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
        >
          Reintentar
        </button>
      </div>
    )
  }

  const { kpis, period, slaCompliance, recentAlerts, myQueue, generatedAt } = overview
  const rangeLabel = range === '7d' ? '7 días' : range === '30d' ? '30 días' : range === '90d' ? '90 días' : 'todo el período'
  const firstName = user?.firstName || 'equipo'
  const criticalCases = overview.casesByCriticidad
    .filter((item: any) => {
      const l = String(item.label || '').toLowerCase()
      return l.includes('crit') || l.includes('alt') || l.includes('high') || l.includes('urg')
    })
    .reduce((total: number, item: any) => total + item.count, 0)

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" aria-labelledby="dashboard-title">
      <header className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600 mb-2">{company?.name || 'ComCenter'}</p>
          <h1 id="dashboard-title" className="text-2xl md:text-3xl font-bold tracking-tight text-slate-950 dark:text-gray-100">
            Buenos días, {firstName} <span aria-hidden="true">👋</span>
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">Resumen ejecutivo · Últimos {rangeLabel}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangeFilter value={range} onChange={setRange} />
          <button
            onClick={() => navigate('/analytics')}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            Ver analítica
            <ArrowRight className="w-4 h-4" />
          </button>
          <LiveIndicator lastUpdatedAt={dataUpdatedAt} isRefreshing={isFetching} />
        </div>
      </header>

      <section aria-label="Indicadores principales" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <ExecutiveKpiCard label="Casos activos" value={kpis.openCases} detail={`${myQueue.openCases} asignados a ti`} icon={FolderOpen} tone="blue" onClick={() => navigate('/cases?status=open')} />
        <ExecutiveKpiCard label="Alertas críticas" value={kpis.criticalAlerts} detail={kpis.criticalAlerts ? 'Requieren acción inmediata' : 'Sin alertas críticas'} icon={Bell} tone="red" onClick={() => navigate('/alerts?severity=critical')} />
        <ExecutiveKpiCard label="Correos pendientes" value={kpis.unprocessedEmails} detail="Por clasificar" icon={Mail} tone="blue" onClick={() => navigate('/emails?status=received')} />
        <ExecutiveKpiCard label="Cumplimiento SLA" value={`${slaCompliance.rate}%`} detail={`${slaCompliance.totalOpen} casos abiertos`} icon={Clock} tone="green" onClick={() => navigate('/cases')} />
      </section>

      <PriorityPanel
        criticalCases={criticalCases}
        dueSoon={(slaCompliance.atRisk || 0) + (slaCompliance.breached || 0)}
        pendingEmails={kpis.unprocessedEmails}
        onCritical={() => navigate('/cases?priority=critical')}
        onDueSoon={() => navigate('/cases?slaStatus=at_risk')}
        onEmails={() => navigate('/emails?status=received')}
      />

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4" aria-label="Estado operativo y trabajo">
        <OperationalStatusCard 
          data={slaCompliance} 
          onClick={() => navigate('/cases')} 
          onTrackClick={() => navigate('/cases?slaStatus=on_track')}
          onAtRiskClick={() => navigate('/cases?slaStatus=at_risk')}
          onBreachedClick={() => navigate('/cases?slaStatus=breached')}
        />
        <MyWorkCard data={myQueue} period={period} onClick={() => navigate('/cases?assignee=me&status=open')} />
      </section>

      {recentAlerts.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-gray-100">Actividad reciente</h2>
              <p className="text-sm text-slate-500">Alertas que requieren seguimiento</p>
            </div>
            <button onClick={() => navigate('/alerts')} className="text-sm font-semibold text-primary-600 hover:text-primary-700">Ver todas</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {recentAlerts.slice(0, 3).map((alert: any) => (
              <button key={alert.id} onClick={() => navigate(`/alerts/${alert.id}`)} className="text-left rounded-lg border border-slate-200 p-3 hover:border-primary-300 hover:bg-primary-50/40 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-xs text-slate-500">{alert.severity?.label || alert.type?.label || 'Alerta'}</span>
                </div>
                <p className="text-sm font-medium text-slate-900 truncate dark:text-gray-100">{alert.title}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      <p className="text-xs text-slate-400 text-center">Actualizado a las {new Date(generatedAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</p>
    </div>
  )
}

function LegacyDashboardPage() {
  const navigate = useNavigate()
  const { impersonateMailboxId } = useImpersonation()
  const [range, setRange] = useState<DateRange>('30d')
  const { data: overview, isLoading, dataUpdatedAt, isFetching } = useDashboardOverview(range, impersonateMailboxId || undefined)

  const sortedCriticidad = useMemo(() => {
    const data = (overview as any)?.casesByCriticidad || []
    return [...data].sort((a: any, b: any) => {
      const ai = CRITICIDAD_ORDER.indexOf(a.label)
      const bi = CRITICIDAD_ORDER.indexOf(b.label)
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
    })
  }, [overview])

  if (isLoading || !overview) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="sr-only">Cargando dashboard…</span>
      </div>
    )
  }

  const { kpis, period, slaCompliance, casesByStatus, alertsBySeverity, recentAlerts, emailStats, casesByCriticidad, casesByMailbox, myQueue, generatedAt } = overview

  const rangeLabel = range === '7d' ? '7 días' : range === '30d' ? '30 días' : range === '90d' ? '90 días' : 'todo el período'

  return (
    <div className="space-y-6" aria-labelledby="dashboard-title">
      {/* ── HEADER ── */}
      <header className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 id="dashboard-title" className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {period.totalCases} casos en {rangeLabel} · {period.resolutionRate}% resueltos
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangeFilter value={range} onChange={setRange} />
          <LiveIndicator lastUpdatedAt={dataUpdatedAt} isRefreshing={isFetching} />
        </div>
      </header>

      {/* ── SECCIÓN 1: KPIs ── */}
      <section aria-label="Indicadores clave">
        <h2 className="sr-only">Indicadores clave</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPICard
            label="Casos activos"
            value={kpis.openCases}
            icon={FolderOpen}
            tone="amber"
            href="/cases?status=open"
            hint={myQueue.openCases > 0 ? `${myQueue.openCases} asignados a ti` : undefined}
          />
          <KPICard
            label="Alertas activas"
            value={kpis.activeAlerts}
            subValue={kpis.criticalAlerts > 0 ? `${kpis.criticalAlerts} críticas` : undefined}
            icon={Bell}
            tone={kpis.criticalAlerts > 0 ? 'red' : 'amber'}
            href="/alerts?severity=critical"
          />
          <KPICard
            label="Emails sin procesar"
            value={kpis.unprocessedEmails}
            icon={Mail}
            tone="blue"
            href="/emails?label=UNREAD"
          />
          <KPICard
            label="Tiempo medio resolución"
            value={kpis.avgResolutionHours != null ? `${kpis.avgResolutionHours}h` : 'N/A'}
            icon={Clock}
            tone="green"
            href="/cases"
            hint={period.resolutionRate > 0 ? `${period.resolutionRate}% resueltos` : undefined}
          />
        </div>
      </section>

      {/* ── SECCIÓN 2: GRÁFICOS PRINCIPALES ── */}
      <section aria-label="Actividad del sistema">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ToggleableChart
            title="Casos por estado"
            icon={FolderOpen}
            iconColor="text-primary-600"
            viewAllHref="/cases"
            data={casesByStatus.map(s => ({
              ...s,
              shortName: (s.label || s.name || '').length > 12 ? (s.label || s.name).slice(0, 11) + '…' : (s.label || s.name),
            }))}
            dataKey={"count"}
            nameKey={"shortName"}
            fullNameKey={"label"}
            onItemClick={(d) => navigate(`/cases?statusId=${d.id}`)}
            ariaLabelTemplate={(d) => `${d.label || d.name}: ${d.count} casos`}
            totalLabel="casos"
          />

          <ToggleableChart
            title="Alertas por severidad"
            icon={Bell}
            iconColor="text-red-600"
            viewAllHref="/alerts"
            data={alertsBySeverity}
            dataKey={"count"}
            nameKey={"label"}
            fullNameKey={"label"}
            onItemClick={(d) => navigate(`/alerts?severity=${d.name}`)}
            ariaLabelTemplate={(d) => `${d.label}: ${d.count} alertas`}
            totalLabel="alertas"
            chartKind="severity"
          />
        </div>
      </section>

      {/* ── SECCIÓN 3: SLA + MI COLA ── */}
      <section aria-label="Acción requerida">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SLA compliance */}
          <Card
            header={
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600" aria-hidden="true" />
                  Cumplimiento SLA
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {slaCompliance.totalOpen} casos abiertos
                </span>
              </div>
            }
          >
            <SLABar data={slaCompliance} />
          </Card>

          {/* Mi cola */}
          <Card
            header={
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-blue-600" aria-hidden="true" />
                  Mi cola de trabajo
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(myQueue.href)}
                  rightIcon={<ChevronRight className="w-3 h-3" aria-hidden="true" />}
                >
                  Ver todos
                </Button>
              </div>
            }
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{myQueue.openCases}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Casos asignados abiertos</p>
                </div>
                <FolderOpen className="w-8 h-8 text-blue-400" aria-hidden="true" />
              </div>
              {myQueue.dueSoon > 0 && (
                <button
                  onClick={() => navigate(myQueue.href)}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                  aria-label={`${myQueue.dueSoon} casos por vencer en 24h. Ver detalle.`}
                >
                  <AlertTriangle className="w-5 h-5 text-amber-600" aria-hidden="true" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {myQueue.dueSoon} caso{myQueue.dueSoon === 1 ? '' : 's'} por vencer
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">En las próximas 24h</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-amber-500" aria-hidden="true" />
                </button>
              )}
              {myQueue.openCases === 0 && myQueue.dueSoon === 0 && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" aria-hidden="true" />
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">No tienes casos pendientes</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </section>

      {/* ── SECCIÓN 4: CRITICIDAD + ALERTAS ── */}
      <section aria-label="Análisis por criticidad y alertas">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Casos por criticidad */}
          <Card
            header={
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-orange-600" aria-hidden="true" />
                  Emails por criticidad
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/emails')}
                  rightIcon={<ChevronRight className="w-3 h-3" aria-hidden="true" />}
                >
                  Ver emails
                </Button>
              </div>
            }
          >
            {sortedCriticidad.length === 0 ? (
              <EmptyState icon={Tag} message="Sin datos de criticidad" tone="gray" />
            ) : (
              <div className="space-y-2">
                {sortedCriticidad.map((c) => {
                  const total = sortedCriticidad.reduce((s, x) => s + x.count, 0)
                  const pct = total > 0 ? Math.round((c.count / total) * 100) : 0
                  const color = CRITICIDAD_COLORS[c.label] || '#94a3b8'
                  return (
                    <button
                      key={c.label}
                      onClick={() => navigate(c.href)}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{c.label}</span>
                          <span className="text-xs font-bold text-gray-900 dark:text-gray-100">{c.count}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: color }}
                          />
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </Card>

          {/* Alertas recientes */}
          <Card
            header={
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" aria-hidden="true" />
                  Alertas recientes
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/alerts')}
                  rightIcon={<ChevronRight className="w-3 h-3" aria-hidden="true" />}
                >
                  Ver todas
                </Button>
              </div>
            }
          >
            {recentAlerts.length === 0 ? (
              <EmptyState icon={CheckCircle2} message="No hay alertas activas" tone="emerald" />
            ) : (
              <ul className="space-y-1.5" role="list">
                {recentAlerts.map((alert) => (
                  <li key={alert.id}>
                    <button
                      onClick={() => navigate(`/alerts/${alert.id}`)}
                      className="w-full text-left flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                      {alert.severity?.color && (
                        <span
                          className="mt-1 w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: alert.severity.color }}
                          aria-hidden="true"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {alert.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {alert.severity?.label || alert.type?.label || 'Alerta'} ·{' '}
                          {new Date(alert.createdAt).toLocaleDateString('es-CO', {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </section>

      {/* ── SECCIÓN 5: TENDENCIA EMAILS + MAILBOXES ── */}
      <section aria-label="Tendencias y distribución">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Emails por día */}
          <div className="lg:col-span-2">
            <Card
              header={
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Inbox className="w-5 h-5 text-primary-600" aria-hidden="true" />
                    Emails recibidos ({rangeLabel})
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      {emailStats.total} total
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      {emailStats.unread} sin leer
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      {emailStats.processed} procesados
                    </span>
                  </div>
                </div>
              }
            >
              {emailStats.byDay.length === 0 ? (
                <EmptyState icon={Inbox} message="Sin datos en este período" tone="gray" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={emailStats.byDay} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="emailGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      tickFormatter={(d) => new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                    />
                    <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} allowDecimals={false} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null
                        const p = payload[0].payload
                        return (
                          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs">
                            <p className="font-semibold text-gray-900">
                              {new Date(p.date).toLocaleDateString('es-CO', { weekday: 'short', day: '2-digit', month: 'short' })}
                            </p>
                            <p className="text-gray-600">{p.count} emails</p>
                          </div>
                        )
                      }}
                    />
                    <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} fill="url(#emailGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* Casos por mailbox */}
          <Card
            header={
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Send className="w-5 h-5 text-indigo-600" aria-hidden="true" />
                Casos por buzón
              </h3>
            }
          >
            {!casesByMailbox || casesByMailbox.length === 0 ? (
              <EmptyState icon={Mail} message="Sin datos" tone="gray" />
            ) : (
              <div className="space-y-2">
                {casesByMailbox.map((mb) => {
                  const total = casesByMailbox.reduce((s, x) => s + x.count, 0)
                  const pct = total > 0 ? Math.round((mb.count / total) * 100) : 0
                  return (
                    <div key={mb.mailbox} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[70%]">{mb.mailbox}</span>
                        <span className="text-xs font-bold text-gray-900 dark:text-gray-100">{mb.count}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-indigo-500 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </div>
      </section>

      <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
        Actualizado a las {new Date(generatedAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </p>
    </div>
  )
}

function ExecutiveKpiCard({ label, value, detail, icon: Icon, tone, onClick }: {
  label: string
  value: number | string
  detail?: string
  icon: any
  tone: 'blue' | 'red' | 'green' | 'amber' | 'purple' | 'slate'
  onClick: () => void
}) {
  return (
    <IndicatorCard
      label={label}
      value={value}
      detail={detail}
      icon={Icon}
      tone={tone}
      onClick={onClick}
    />
  )
}

function PriorityPanel({ criticalCases, dueSoon, pendingEmails, onCritical, onDueSoon, onEmails }: {
  criticalCases: number
  dueSoon: number
  pendingEmails: number
  onCritical: () => void
  onDueSoon: () => void
  onEmails: () => void
}) {
  const items = [
    { label: 'Casos críticos', detail: 'Requieren acción inmediata', value: criticalCases, icon: ShieldAlert, color: 'red', onClick: onCritical },
    { label: 'SLA en riesgo', detail: 'Próximos a vencer o vencidos', value: dueSoon, icon: Clock, color: 'amber', onClick: onDueSoon },
    { label: 'Pendientes por clasificar', detail: 'Requieren atención', value: pendingEmails, icon: Mail, color: 'blue', onClick: onEmails },
  ] as const
  const colors = {
    red: { icon: 'bg-red-50 text-red-500', value: 'text-red-600', button: 'border-red-100 hover:border-red-300' },
    amber: { icon: 'bg-amber-50 text-amber-500', value: 'text-amber-600', button: 'border-amber-100 hover:border-amber-300' },
    blue: { icon: 'bg-blue-50 text-blue-500', value: 'text-blue-600', button: 'border-blue-100 hover:border-blue-300' },
  }
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:bg-gray-900 dark:border-gray-700" aria-labelledby="priority-title">
      <div className="mb-5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 text-red-500" />
        <div><h2 id="priority-title" className="text-base font-semibold text-slate-900 dark:text-gray-100">Atención prioritaria</h2><p className="text-sm text-slate-500">Lo más urgente que requiere tu atención.</p></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-gray-700">
        {items.map((item) => {
          const color = colors[item.color]
          return <button key={item.label} onClick={item.onClick} className={cn('flex items-center gap-4 px-2 py-3 text-left md:px-5 first:pl-0 last:pr-0 group', color.button)}>
            <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-full', color.icon)}><item.icon className="w-6 h-6" /></div>
            <div className="min-w-0"><p className={cn('text-3xl font-bold', color.value)}>{item.value}</p><p className="text-sm font-medium text-slate-800 dark:text-gray-200">{item.label}</p><p className="text-xs text-slate-500 mt-1">{item.detail}</p><span className="mt-3 inline-flex text-xs font-semibold text-primary-600">Ver detalle <ArrowRight className="ml-1 w-3 h-3" /></span></div>
          </button>
        })}
      </div>
    </section>
  )
}

function OperationalStatusCard({ 
  data, 
  onClick,
  onTrackClick,
  onAtRiskClick,
  onBreachedClick
}: { 
  data: { onTrack: number; atRisk: number; breached: number; rate: number; totalOpen: number }
  onClick: () => void
  onTrackClick?: () => void
  onAtRiskClick?: () => void
  onBreachedClick?: () => void
}) {
  const total = Math.max(data.totalOpen, 1)
  const chart = `conic-gradient(#10b981 0 ${(data.onTrack / total) * 100}%, #f59e0b ${(data.onTrack / total) * 100}% ${((data.onTrack + data.atRisk) / total) * 100}%, #ef4444 ${((data.onTrack + data.atRisk) / total) * 100}% 100%)`
  const rows = [
    { label: 'En tiempo', value: data.onTrack, dot: 'bg-emerald-500', action: onTrackClick },
    { label: 'En riesgo', value: data.atRisk, dot: 'bg-amber-500', action: onAtRiskClick },
    { label: 'Vencidos', value: data.breached, dot: 'bg-red-500', action: onBreachedClick },
  ]
  return <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:bg-gray-900 dark:border-gray-700">
    <div className="flex items-start gap-3 mb-5"><CheckCircle2 className="w-5 h-5 mt-0.5 text-primary-600" /><div><h2 className="text-base font-semibold text-slate-900 dark:text-gray-100">Estado operativo</h2><p className="text-sm text-slate-500">Panorama general de la operación.</p></div></div>
    <div className="flex flex-col sm:flex-row items-center gap-7">
      <div className="relative h-36 w-36 shrink-0 rounded-full" style={{ background: chart }}><div className="absolute inset-4 flex flex-col items-center justify-center rounded-full bg-white dark:bg-gray-900"><strong className="text-2xl text-slate-950 dark:text-gray-100">{data.rate}%</strong><span className="text-xs text-slate-500">SLA</span></div></div>
      <div className="w-full space-y-2">
        {rows.map((r) => (
          <button 
            key={r.label} 
            onClick={r.action || onClick} 
            className="w-full flex items-center justify-between border-b border-slate-100 py-2 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-800/60 transition-colors last:border-0 dark:border-gray-700 text-left"
          >
            <span className="flex items-center gap-2 text-sm text-slate-600 dark:text-gray-300">
              <span className={cn('h-2 w-2 rounded-full', r.dot)} />{r.label}
            </span>
            <strong className="text-sm text-slate-900 dark:text-gray-100">{r.value}</strong>
          </button>
        ))}
      </div>
    </div>
    <button onClick={onClick} className="mt-5 w-full border-t border-slate-100 pt-4 text-center text-sm font-semibold text-primary-600 hover:text-primary-700 dark:border-gray-700">Ver detalle operativo <ArrowRight className="inline w-4 h-4 ml-1" /></button>
  </section>
}

function MyWorkCard({ data, period, onClick }: { data: { openCases: number; dueSoon: number }; period: { totalCases: number; resolutionRate: number }; onClick: () => void }) {
  return <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:bg-gray-900 dark:border-gray-700">
    <div className="flex items-start gap-3 mb-5"><UserCircleIcon className="w-5 h-5 mt-0.5 text-primary-600" /><div><h2 className="text-base font-semibold text-slate-900 dark:text-gray-100">Mi trabajo</h2><p className="text-sm text-slate-500">Tu próxima acción prioritaria.</p></div></div>
    <div className="rounded-lg border border-primary-100 bg-primary-50/40 p-5 dark:bg-primary-900/20 dark:border-primary-800"><div className="flex items-center justify-between gap-4"><div><span className="text-[10px] font-bold uppercase tracking-wider text-primary-700 dark:text-primary-300">Resumen asignado</span><p className="mt-2 text-2xl font-bold text-slate-950 dark:text-gray-100">{data.openCases} casos abiertos</p><p className="mt-1 text-sm text-slate-500">{data.dueSoon} próximos a vencer · {period.resolutionRate}% resueltos en el período</p></div><Clock className="w-9 h-9 text-primary-400 shrink-0" /></div></div>
    <button onClick={onClick} className="mt-5 w-full text-center text-sm font-semibold text-primary-600 hover:text-primary-700">Ver mis casos <ArrowRight className="inline w-4 h-4 ml-1" /></button>
  </section>
}

function UserCircleIcon(props: any) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}><circle cx="12" cy="8" r="3.5" /><path d="M4.5 20c.8-3.2 3.3-5 7.5-5s6.7 1.8 7.5 5" /></svg>
}

function KPICard({ label, value, subValue, icon: Icon, tone, href, hint }: {
  label: string
  value: number | string
  subValue?: string
  icon: any
  tone: 'amber' | 'red' | 'blue' | 'green' | 'slate' | 'purple'
  href: string
  hint?: string
}) {
  const navigate = useNavigate()
  return (
    <IndicatorCard
      label={label}
      value={value}
      detail={subValue || hint}
      icon={Icon}
      tone={tone}
      onClick={() => navigate(href)}
    />
  )
}

function DateRangeFilter({ value, onChange }: { value: DateRange; onChange: (r: DateRange) => void }) {
  return (
    <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-0.5" role="tablist" aria-label="Período">
      <Calendar className="w-3.5 h-3.5 text-gray-400 ml-2" aria-hidden="true" />
      {RANGE_OPTIONS.map((opt) => {
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            role="tab"
            aria-selected={active}
            className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              active
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

interface ChartItem {
  id: string
  name: string
  shortName?: string
  label?: string
  color?: string
  count: number
}

function ToggleableChart<T extends ChartItem>({
  title, icon: Icon, iconColor, viewAllHref, data, dataKey, nameKey, fullNameKey,
  onItemClick, ariaLabelTemplate, totalLabel, chartKind = 'default',
}: {
  title: string
  icon: any
  iconColor: string
  viewAllHref: string
  data: T[]
  dataKey: string
  nameKey: string
  fullNameKey?: string
  onItemClick: (item: T) => void
  ariaLabelTemplate: (item: T) => string
  totalLabel: string
  chartKind?: 'default' | 'severity'
}): JSX.Element {
  const [chartType, setChartType] = useState<DonutBarTable | BarAreaTable>(chartKind === 'severity' ? 'donut' : 'bar')
  const navigate = useNavigate()
  const total = data.reduce((acc, d) => acc + d.count, 0)

  return (
    <Card
      header={
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 min-w-0">
            <Icon className={cn('w-5 h-5 flex-shrink-0', iconColor)} aria-hidden="true" />
            <span className="truncate">{title}</span>
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({total})</span>
          </h3>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {chartKind === 'severity' ? (
              <ChartTypeToggle
                value={chartType}
                onChange={setChartType}
                options={[
                  { value: 'donut', label: 'Donut', icon: AreaIcon },
                  { value: 'bar', label: 'Barras', icon: BarChart3 },
                  { value: 'table', label: 'Tabla', icon: Table2 },
                ]}
              />
            ) : (
              <ChartTypeToggle
                value={chartType}
                onChange={setChartType}
                options={[
                  { value: 'bar', label: 'Barras', icon: BarChart3 },
                  { value: 'area', label: 'Área', icon: AreaIcon },
                  { value: 'table', label: 'Tabla', icon: Table2 },
                ]}
              />
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(viewAllHref)}
              rightIcon={<ChevronRight className="w-3 h-3" aria-hidden="true" />}
              aria-label={`Ver todos: ${title}`}
            >
              Ver todos
            </Button>
          </div>
        </div>
      }
    >
      {data.length === 0 ? (
        <EmptyState icon={BarChart3} message="Sin datos para mostrar" tone="gray" />
      ) : chartType === 'table' ? (
        <DataTable data={data} onRowClick={onItemClick} ariaLabelTemplate={ariaLabelTemplate} totalLabel={totalLabel} fullNameKey={fullNameKey} />
      ) : chartKind === 'severity' && chartType === 'donut' ? (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={85}
              paddingAngle={2}
              dataKey={dataKey}
              nameKey={fullNameKey || nameKey}
              cursor="pointer"
              onClick={(_, idx) => onItemClick(data[idx])}
              label={({ percent }: any) => `${Math.round((percent || 0) * 100)}%`}
              labelLine={false}
            >
              {data.map((entry, idx) => (
                <Cell key={`cell-${idx}`} fill={entry.color || '#94a3b8'} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const p: any = payload[0].payload
                return (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs">
                    <p className="font-semibold text-gray-900">{p[fullNameKey || nameKey]}</p>
                    <p className="text-gray-600">{p[dataKey]} {totalLabel}</p>
                  </div>
                )
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={32}
              iconType="circle"
              wrapperStyle={{ fontSize: 11 }}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : chartType === 'bar' ? (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
            onClick={(e: any) => e?.activePayload?.[0] && onItemClick(e.activePayload[0].payload)}
          >
            <XAxis
              dataKey={nameKey}
              tick={{ fontSize: 10, fill: '#6b7280' }}
              interval={0}
              angle={nameKey === 'shortName' ? 0 : -15}
              textAnchor={nameKey === 'shortName' ? 'middle' : 'end'}
              height={nameKey === 'shortName' ? 30 : 50}
            />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} allowDecimals={false} />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.08)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const p: any = payload[0].payload
                return (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs">
                    <p className="font-semibold text-gray-900">{p[fullNameKey || nameKey]}</p>
                    <p className="text-gray-600">{p[dataKey]} {totalLabel}</p>
                  </div>
                )
              }}
            />
            <Bar dataKey={dataKey} radius={[6, 6, 0, 0]} cursor="pointer" onClick={(d: any) => onItemClick(d)}>
              {data.map((entry, idx) => (
                <Cell key={`bar-${idx}`} fill={entry.color || '#3b82f6'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id={`area-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey={nameKey}
              tick={{ fontSize: 10, fill: '#6b7280' }}
              interval={0}
              angle={nameKey === 'shortName' ? 0 : -15}
              textAnchor={nameKey === 'shortName' ? 'middle' : 'end'}
              height={nameKey === 'shortName' ? 30 : 50}
            />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} allowDecimals={false} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const p: any = payload[0].payload
                return (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs">
                    <p className="font-semibold text-gray-900">{p[fullNameKey || nameKey]}</p>
                    <p className="text-gray-600">{p[dataKey]} {totalLabel}</p>
                  </div>
                )
              }}
            />
            <Area type="monotone" dataKey={dataKey} stroke="#3b82f6" strokeWidth={2} fill={`url(#area-${dataKey})`} />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {/* Tabla accesible sr-only */}
      <table className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr><th>Estado</th><th>Cantidad</th></tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              <td>{String(row[(fullNameKey || nameKey) as keyof T] ?? '')}</td>
              <td>{String(row[dataKey as keyof T] ?? '')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

function ChartTypeToggle({ value, onChange, options }: {
  value: string
  onChange: (v: any) => void
  options: { value: string; label: string; icon: any }[]
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-0.5" role="tablist" aria-label="Tipo de visualización">
      {options.map((opt) => {
        const Icon = opt.icon
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            role="tab"
            aria-selected={active}
            title={opt.label}
            className={cn(
              'p-1 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              active
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            )}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="sr-only">{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}

function DataTable<T extends ChartItem>({ data, onRowClick, ariaLabelTemplate, totalLabel, fullNameKey }: {
  data: T[]
  onRowClick: (item: T) => void
  ariaLabelTemplate: (item: T) => string
  totalLabel: string
  fullNameKey?: string
}) {
  const total = data.reduce((acc, d) => acc + d.count, 0)
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="text-left py-2 px-2 font-medium text-gray-500 dark:text-gray-400">Nombre</th>
            <th className="text-right py-2 px-2 font-medium text-gray-500 dark:text-gray-400">Cantidad</th>
            <th className="text-right py-2 px-2 font-medium text-gray-500 dark:text-gray-400">%</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const pct = total > 0 ? Math.round((row.count / total) * 100) : 0
            return (
              <tr
                key={row.id || idx}
                onClick={() => onRowClick(row)}
                className="border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors focus-within:bg-gray-50"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onRowClick(row)}
                aria-label={ariaLabelTemplate(row)}
              >
                <td className="py-2 px-2">
                  <div className="flex items-center gap-2">
                    {row.color && (
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }} aria-hidden="true" />
                    )}
                    <span className="text-gray-900 dark:text-gray-100 truncate">
                      {String(row[(fullNameKey || 'name') as keyof T] ?? '')}
                    </span>
                  </div>
                </td>
                <td className="py-2 px-2 text-right font-medium text-gray-900 dark:text-gray-100">
                  {row.count}
                </td>
                <td className="py-2 px-2 text-right text-gray-500 dark:text-gray-400">
                  {pct}%
                </td>
              </tr>
            )
          })}
          <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-medium">
            <td className="py-2 px-2 text-gray-700 dark:text-gray-300">Total</td>
            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{total}</td>
            <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{totalLabel}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function SLABar({ data }: { data: { onTrack: number; atRisk: number; breached: number; rate: number; totalOpen: number } }) {
  const navigate = useNavigate()
  if (data.totalOpen === 0) {
    return <EmptyState icon={CheckCircle2} message="No hay casos abiertos" tone="emerald" />
  }
  const total = data.totalOpen
  const pct = (n: number) => total > 0 ? (n / total) * 100 : 0

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">{data.rate}%</span>
        <span className="text-sm text-gray-500 dark:text-gray-400 mb-1">en tiempo</span>
      </div>

      <div
        className="flex h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800"
        role="img"
        aria-label={`Cumplimiento SLA: ${data.onTrack} en tiempo, ${data.atRisk} en riesgo, ${data.breached} vencidos`}
      >
        <div
          className="bg-emerald-500 transition-all"
          style={{ width: `${pct(data.onTrack)}%` }}
          title={`${data.onTrack} en tiempo`}
        />
        <div
          className="bg-amber-500 transition-all"
          style={{ width: `${pct(data.atRisk)}%` }}
          title={`${data.atRisk} en riesgo`}
        />
        <div
          className="bg-red-500 transition-all"
          style={{ width: `${pct(data.breached)}%` }}
          title={`${data.breached} vencidos`}
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <SLALegendItem color="emerald" label="En tiempo" value={data.onTrack} onClick={() => navigate('/cases?status=open&sla=onTrack')} />
        <SLALegendItem color="amber" label="En riesgo" value={data.atRisk} onClick={() => navigate('/cases?status=open&sla=atRisk')} />
        <SLALegendItem color="red" label="Vencidos" value={data.breached} onClick={() => navigate('/cases?status=open&sla=breached')} />
      </div>
    </div>
  )
}

function SLALegendItem({ color, label, value, onClick }: { color: 'emerald' | 'amber' | 'red'; label: string; value: number; onClick: () => void }) {
  const colorClasses = {
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100',
    amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-100',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100',
  }
  return (
    <button
      onClick={onClick}
      className={cn('flex flex-col p-2.5 rounded-lg border text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500', colorClasses[color])}
      aria-label={`${label}: ${value} casos. Ver detalle.`}
    >
      <span className="text-xl font-bold">{value}</span>
      <span className="text-xs">{label}</span>
    </button>
  )
}

function EmptyState({ icon: Icon, message, tone }: { icon: any; message: string; tone: 'gray' | 'emerald' }) {
  const toneClasses = tone === 'emerald'
    ? 'text-emerald-500 dark:text-emerald-400'
    : 'text-gray-400 dark:text-gray-500'
  return (
    <div className="flex flex-col items-center justify-center py-6 text-sm">
      <Icon className={cn('w-8 h-8 mb-2', toneClasses)} aria-hidden="true" />
      <p className="text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  )
}
